import {
  CATALOG_SUPPLIER_IMAGE_LIMIT,
  type ConflictCode,
  type ForbiddenCode,
  type ServiceUnavailableCode,
  type ValidationCode,
} from '@amore/shared'

const messages = {
  'draft-conflict':
    'Údaje mezitím někdo změnil. Vaše rozepsané změny zůstávají zachované. Načtěte aktuální verzi a porovnejte je.',
  'not-found': 'Požadovaný záznam nebyl nalezen.',
  unauthorized: 'Pro pokračování se přihlaste.',
  forbidden: 'K této akci nemáte oprávnění.',
  'workspace-write-required': 'Máte přístup pouze pro čtení.',
  'workspace-owner-required': 'Tuto akci může provést pouze vlastník svatby.',
  'workspace-last-owner': 'Svatba musí mít alespoň jednoho vlastníka.',
  'workspace-slug-unavailable': 'Tato adresa svatby není dostupná. Zvolte jinou.',
  'workspace-member-exists': 'Tento e-mail už patří členovi svatby.',
  'invitation-pending': 'Na tento e-mail už čeká pozvánka.',
  'invitation-not-pending': 'Zrušit lze pouze čekající pozvánku.',
  'invitation-already-used': 'Pozvánka už byla použita. Obnovte stránku.',
  'invitation-email-mismatch': 'Tato pozvánka patří jinému e-mailu.',
  'impersonation-read-only': 'V režimu náhledu za uživatele nelze nic měnit.',
  'admin-required': 'Tato akce je dostupná pouze administrátorům.',
  'venue-already-added': 'Toto místo už je ke svatbě přidané.',
  'catalog-supplier-duplicate': 'Tento dodavatel už v katalogu je.',
  'wedding-supplier-exists': 'Tento dodavatel už je mezi vašimi dodavateli.',
  'budget-category-not-empty':
    'Kategorii s položkami nelze smazat. Nejprve položky přesuňte nebo smažte.',
  'budget-system-category-delete': 'Základní kategorii rozpočtu nelze smazat.',
  'budget-linked-expense-delete':
    'Položku propojenou s dodavatelem upravte nebo smažte v jeho detailu.',
  'budget-supplier-already-linked': 'Tento dodavatel už je propojený s jinou položkou rozpočtu.',
  'document-not-ready': 'Dokument ještě není připravený.',
  'catalog-supplier-image-attached': 'Tato fotka už u dodavatele je.',
  'invalid-input': 'Zkontrolujte zadané údaje.',
  'invalid-assignee': 'Vyberte aktivního člena svatby.',
  'automatic-task-status-managed':
    'Stav tohoto úkolu se mění automaticky podle údajů ve svatebním plánu.',
  'budget-payment-exceeds-expense':
    'Platby nemohou překročit celkovou částku položky. Nejprve upravte částku položky.',
  'budget-expense-below-payments': 'Částka položky nemůže být nižší než součet jejích plateb.',
  'contact-method-required': 'Doplňte alespoň telefon nebo e-mail.',
  'remote-image-rejected':
    'Obrázek z odkazu se nepodařilo stáhnout. Odkaz musí vést přímo na obrázek JPEG, PNG, WebP nebo AVIF do 10 MB.',
  'catalog-supplier-image-limit': `Dodavatel může mít nejvýše ${CATALOG_SUPPLIER_IMAGE_LIMIT} fotek.`,
  'upload-mismatch': 'Nahraný soubor neodpovídá vybranému souboru. Zkuste ho nahrát znovu.',
  'dependency-unavailable': 'Služba je dočasně nedostupná. Zkuste to znovu později.',
  'storage-unavailable': 'Nahrávání dokumentů zatím není dostupné.',
  'workspace-create-failed': 'Svatbu se nepodařilo vytvořit.',
  'workspace-update-failed': 'Svatbu se nepodařilo aktualizovat.',
  'wedding-create-failed': 'Údaje o svatbě se nepodařilo uložit.',
} satisfies Record<
  | ConflictCode
  | ForbiddenCode
  | ValidationCode
  | ServiceUnavailableCode
  | 'not-found'
  | 'unauthorized',
  string
>

function hasMessage(code: string): code is keyof typeof messages {
  return Object.hasOwn(messages, code)
}

export function problemMessage(problem: { code: string; status: number }) {
  if (hasMessage(problem.code)) return messages[problem.code]
  if (problem.status === 401) return messages.unauthorized
  if (problem.status === 403) return messages.forbidden
  if (problem.status === 404) return messages['not-found']
  if (problem.status === 429) return 'Příliš mnoho pokusů. Zkuste to znovu později.'
  return 'Požadavek se nepodařilo dokončit. Zkuste to prosím znovu.'
}

export function authErrorMessage(error: { code?: string }, fallback: string) {
  switch (error.code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
    case 'INVALID_PASSWORD':
      return 'E-mail nebo heslo nejsou správné.'
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return 'Účet s tímto e-mailem už existuje.'
    case 'EMAIL_NOT_VERIFIED':
      return 'Nejprve ověřte svůj e-mail.'
    default:
      return fallback
  }
}
